import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { format, parseISO, isSameDay, startOfMonth, endOfMonth, eachDayOfInterval } from "date-fns";
import { toast } from "sonner";
import { 
  Leaf, Calendar as CalendarIcon, Clock, MapPin, DollarSign, LogOut, CheckCircle, Facebook, 
  CalendarPlus, Scissors, ChevronRight, CreditCard, Pause, Play, X, Sparkles, AlertTriangle,
  AlertCircle, ChevronLeft, MessageCircle, Send, Pencil, Phone, Home, User as UserIcon, Timer, Edit3, FileText,
  Trash2, Droplets
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import AddToCalendarModal from "@/components/AddToCalendarModal";
import BookingWizard from "@/components/BookingWizard";
import { AddressGate, QuotePromptDialog } from "@/components/AddressGate";
import { triggerMow } from "@/components/MowerOverlay";

const API = "/api";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_a07fe825-978d-42af-ad0d-66b05bf66e9f/artifacts/6h7nm3ap_Photoroom_20260215_225825.png";

const CustomerPortal = () => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calendarModalOpen, setCalendarModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showBookingWizard, setShowBookingWizard] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState({});
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [pausingSubscription, setPausingSubscription] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
  const [subscriptionsEnabled, setSubscriptionsEnabled] = useState(true);
  const [stripeCheckoutEnabled, setStripeCheckoutEnabled] = useState(true);
  const [additionalServicesEnabled, setAdditionalServicesEnabled] = useState(true);
  const [myQuote, setMyQuote] = useState(null);  // Customer's personalized quote pricing
  const [requestingSubscription, setRequestingSubscription] = useState(false);
  
  // Chat state
  const [myCustomer, setMyCustomer] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [discountClaimed, setDiscountClaimed] = useState(false);
  const chatEndRef = useRef(null);

  // Cut request state
  const [showCutRequest, setShowCutRequest] = useState(false);
  const [cutRequestData, setCutRequestData] = useState({
    address: "",
    preferred_date: "",
    preferred_time: "morning",
    estimated_price: "",
    notes: "",
    returning_customer: false,
  });
  const [submittingCutRequest, setSubmittingCutRequest] = useState(false);
  const [myRequests, setMyRequests] = useState([]);
  const [showQuotePrompt, setShowQuotePrompt] = useState(false);
  const quotePromptKey = `zz_quote_prompt_${user?.user_id}`;
  const needsAddress = !loading && !(myCustomer?.address || "").trim();

  // New customers with no quote yet get told how to get a price (once per device)
  useEffect(() => {
    if (loading || needsAddress || myQuote || jobs.length > 0) return;
    if (!localStorage.getItem(quotePromptKey)) setShowQuotePrompt(true);
  }, [loading, needsAddress, myQuote, jobs.length, quotePromptKey]);

  const closeQuotePrompt = () => {
    localStorage.setItem(quotePromptKey, "1");
    setShowQuotePrompt(false);
  };

  const openCutRequest = () => {
    setCutRequestData((d) => ({ ...d, address: d.address || myCustomer?.address || "" }));
    setShowCutRequest(true);
    setTimeout(() => document.querySelector('[data-testid="cut-request-section"]')?.scrollIntoView({ behavior: "smooth" }), 50);
  };

  // CMS content state
  const [cmsContent, setCmsContent] = useState({});

  // Profile editing state
  const [showProfileEdit, setShowProfileEdit] = useState(false);
  const [profileData, setProfileData] = useState({ name: "", phone: "", address: "", yard_size: "", grass_height: "" });
  const [savingProfile, setSavingProfile] = useState(false);

  // Quick schedule state (for subscribers)
  const [showQuickSchedule, setShowQuickSchedule] = useState(false);
  const [quickScheduleTime, setQuickScheduleTime] = useState("morning");
  const [quickScheduleNotes, setQuickScheduleNotes] = useState("");
  const [submittingQuickSchedule, setSubmittingQuickSchedule] = useState(false);

  // Reschedule state
  const [rescheduleJob, setRescheduleJob] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState("");
  const [rescheduleTime, setRescheduleTime] = useState("morning");
  const [submittingReschedule, setSubmittingReschedule] = useState(false);

  // CMS helper - reads content with fallback
  const cms = (section, key, fallback = "") => cmsContent[section]?.[key] || fallback;

  const openCalendarModal = (job) => {
    setSelectedJob(job);
    setCalendarModalOpen(true);
  };

  const closeCalendarModal = () => {
    setCalendarModalOpen(false);
    setSelectedJob(null);
  };

  // Get jobs for a specific date
  const getJobsForDate = (date) => {
    return jobs.filter(job => {
      try {
        const jobDate = parseISO(job.scheduled_date);
        return isSameDay(jobDate, date);
      } catch {
        return false;
      }
    });
  };

  // Get all dates that have scheduled jobs
  const scheduledDates = jobs
    .filter(j => j.status === "scheduled" || j.status === "in_progress")
    .map(j => {
      try {
        return parseISO(j.scheduled_date);
      } catch {
        return null;
      }
    })
    .filter(Boolean);

  useEffect(() => {
    fetchData();
    checkPaymentStatus();
    // Check if user claimed neighborhood discount from landing page
    const claimed = localStorage.getItem("claimed_neighborhood_discount");
    if (claimed) {
      setDiscountClaimed(true);
      localStorage.removeItem("claimed_neighborhood_discount");
    }
  }, []);

  const fetchData = async () => {
    try {
      const [jobsRes, subRes, plansRes, settingsRes, quoteRes, customerRes, cutReqRes, cmsRes] = await Promise.all([
        axios.get(`${API}/jobs`, { withCredentials: true }),
        axios.get(`${API}/subscription/my-subscription`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/subscription/plans`, { withCredentials: true }),
        axios.get(`${API}/settings`, { withCredentials: true }).catch(() => ({ data: { subscriptions_enabled: true } })),
        axios.get(`${API}/quotes/my-quote/latest`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/customers/my-profile`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/cut-requests`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/cms/content`, { withCredentials: true }).catch(() => ({ data: { content: {}, defaults: {} } }))
      ]);
      setJobs(jobsRes.data);
      setSubscription(settingsRes.data.subscriptions_enabled !== false ? subRes.data : null);
      setPlans(plansRes.data);
      setSubscriptionsEnabled(settingsRes.data.subscriptions_enabled !== false);
      setStripeCheckoutEnabled(settingsRes.data.stripe_checkout_enabled !== false);
      setAdditionalServicesEnabled(settingsRes.data.additional_services_enabled !== false);
      setMyQuote(quoteRes.data);
      setMyCustomer(customerRes.data);
      setMyRequests(cutReqRes.data || []);
      
      // Merge CMS content with defaults for the portal
      const cmsData = cmsRes.data;
      const portalDefaults = cmsData.defaults?.portal || {};
      const portalSaved = {};
      // Organize saved content by section -> key
      if (Array.isArray(cmsData.content)) {
        cmsData.content.filter(c => c.page === "portal").forEach(c => {
          if (!portalSaved[c.section]) portalSaved[c.section] = {};
          portalSaved[c.section][c.content_key] = c.value;
        });
      } else if (cmsData.content?.portal) {
        Object.assign(portalSaved, cmsData.content.portal);
      }
      // Merge: defaults first, then saved overrides
      const merged = {};
      Object.keys(portalDefaults).forEach(section => {
        merged[section] = { ...portalDefaults[section] };
      });
      Object.keys(portalSaved).forEach(section => {
        merged[section] = { ...merged[section], ...portalSaved[section] };
      });
      setCmsContent(merged);
      
      // Fetch messages if we have customer profile
      if (customerRes.data?.customer_id) {
        const messagesRes = await axios.get(`${API}/customers/${customerRes.data.customer_id}/messages`, { withCredentials: true }).catch(() => ({ data: [] }));
        setMessages(messagesRes.data);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async () => {
    const sessionId = searchParams.get("session_id");
    const paymentStatus = searchParams.get("payment");
    
    if (sessionId && paymentStatus === "success") {
      setProcessingPayment(true);
      // Poll for payment status
      let attempts = 0;
      const maxAttempts = 5;
      
      const pollStatus = async () => {
        try {
          const response = await axios.get(`${API}/subscription/checkout/status/${sessionId}`, { withCredentials: true });
          if (response.data.payment_status === "paid") {
            toast.success("Subscription activated! Thank you for subscribing.");
            fetchData();
            setProcessingPayment(false);
            // Clear URL params
            window.history.replaceState({}, document.title, window.location.pathname);
          } else if (attempts < maxAttempts) {
            attempts++;
            setTimeout(pollStatus, 2000);
          } else {
            toast.error("Payment verification timed out. Please check your email for confirmation.");
            setProcessingPayment(false);
          }
        } catch (error) {
          console.error("Failed to check payment status:", error);
          setProcessingPayment(false);
        }
      };
      
      pollStatus();
    } else if (paymentStatus === "cancelled") {
      toast.info("Payment was cancelled.");
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  const handleSubscribe = async (planId) => {
    // Check if Stripe checkout is enabled or request mode
    if (stripeCheckoutEnabled) {
      // Stripe checkout mode
      try {
        setProcessingPayment(true);
        const response = await axios.post(`${API}/subscription/checkout`, {
          plan: planId,
          origin_url: window.location.origin
        }, { withCredentials: true });
        
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      } catch (error) {
        console.error("Failed to create checkout:", error);
        toast.error("Failed to start checkout. Please try again.");
        setProcessingPayment(false);
      }
    } else {
      // Request mode - send message to admin
      try {
        setRequestingSubscription(true);
        const response = await axios.post(`${API}/subscription/request`, {
          plan: planId
        }, { withCredentials: true });
        
        toast.success(response.data.message);
      } catch (error) {
        console.error("Failed to send subscription request:", error);
        toast.error("Failed to send request. Please try again.");
      } finally {
        setRequestingSubscription(false);
      }
    }
  };

  const handlePauseSubscription = async () => {
    setPausingSubscription(true);
    try {
      const response = await axios.post(`${API}/subscription/pause`, {}, { withCredentials: true });
      
      // Show detailed message about skipped jobs
      if (response.data.skipped_count > 0) {
        toast.success(`${response.data.message}. ${response.data.skipped_count} scheduled cut(s) have been skipped.`);
      } else {
        toast.success(response.data.message);
      }
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to pause subscription");
    } finally {
      setPausingSubscription(false);
    }
  };

  const handleResumeSubscription = async () => {
    setPausingSubscription(true);
    try {
      const response = await axios.post(`${API}/subscription/resume`, {}, { withCredentials: true });
      
      // Show detailed message about restored jobs
      if (response.data.restored_count > 0) {
        toast.success(`${response.data.message}. ${response.data.restored_count} scheduled cut(s) have been restored.`);
      } else {
        toast.success(response.data.message);
      }
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resume subscription");
    } finally {
      setPausingSubscription(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm("Are you sure you want to cancel your subscription? You will lose your discount benefits.")) {
      return;
    }
    try {
      await axios.post(`${API}/subscription/cancel`, {}, { withCredentials: true });
      toast.success("Subscription cancelled");
      fetchData();
    } catch (error) {
      toast.error("Failed to cancel subscription");
    }
  };

  // Chat handlers
  const handleClaimDiscount = async () => {
    if (!myCustomer?.customer_id) {
      toast.error("Please complete your profile first so we can reach you");
      return;
    }
    const msg = "Hi! I'd like to claim the Neighborhood Discount. I have neighbors in my area who also use (or are interested in) your lawn care services. Can we arrange a group booking for a discount?";
    try {
      await axios.post(`${API}/customers/${myCustomer.customer_id}/messages`, {
        customer_id: myCustomer.customer_id,
        message: msg,
        message_type: "chat"
      }, { withCredentials: true });
      setDiscountClaimed(false);
      setShowChat(true);
      setNewMessage("");
      const messagesRes = await axios.get(`${API}/customers/${myCustomer.customer_id}/messages`, { withCredentials: true });
      setMessages(messagesRes.data);
      toast.success("Discount request sent! We'll get back to you soon.");
    } catch {
      toast.error("Failed to send message. Please try the chat below.");
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !myCustomer?.customer_id) return;
    setSendingMessage(true);
    try {
      const response = await axios.post(`${API}/customers/${myCustomer.customer_id}/messages`, {
        customer_id: myCustomer.customer_id,
        message: newMessage,
        message_type: "chat"
      }, { withCredentials: true });
      setMessages(prev => [...prev, response.data]);
      setNewMessage("");
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (error) {
      console.error("Failed to send message:", error);
      toast.error("Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  const upcomingJobs = jobs.filter(j => j.status === "scheduled" || j.status === "in_progress");
  const pastJobs = jobs.filter(j => j.status === "completed" || j.status === "cancelled");

  const handleSubmitCutRequest = async () => {
    if (!(cutRequestData.address || "").trim()) {
      toast.error("Please enter your address (e.g. 3725 Camp Foster)");
      return;
    }
    if (!cutRequestData.preferred_date || (!cutRequestData.returning_customer && !cutRequestData.estimated_price)) {
      toast.error(cutRequestData.returning_customer ? "Please select a date" : "Please fill in the date and your estimated price");
      return;
    }
    setSubmittingCutRequest(true);
    try {
      await axios.post(`${API}/cut-requests`, cutRequestData, { withCredentials: true });
      toast.success("Cut request submitted! We'll review it shortly.");
      triggerMow();
      setShowCutRequest(false);
      setCutRequestData({ address: "", preferred_date: "", preferred_time: "morning", estimated_price: "", notes: "", returning_customer: false });
      // Refresh requests
      const res = await axios.get(`${API}/cut-requests`, { withCredentials: true }).catch(() => ({ data: [] }));
      setMyRequests(res.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit request. Please try again.");
    } finally {
      setSubmittingCutRequest(false);
    }
  };

  const handleQuickSchedule = async () => {
    if (!selectedCalendarDate) return;
    setSubmittingQuickSchedule(true);
    try {
      await axios.post(`${API}/cut-requests`, {
        address: myCustomer?.address || "",
        preferred_date: format(selectedCalendarDate, "yyyy-MM-dd"),
        preferred_time: quickScheduleTime,
        estimated_price: "",
        notes: quickScheduleNotes,
        returning_customer: true,
      }, { withCredentials: true });
      toast.success("Cut scheduled! Pending admin approval.");
      triggerMow();
      setShowQuickSchedule(false);
      setQuickScheduleTime("morning");
      setQuickScheduleNotes("");
      const res = await axios.get(`${API}/cut-requests`, { withCredentials: true }).catch(() => ({ data: [] }));
      setMyRequests(res.data || []);
    } catch {
      toast.error("Failed to schedule cut. Please try again.");
    } finally {
      setSubmittingQuickSchedule(false);
    }
  };

  const handleReschedule = async () => {
    if (!rescheduleJob || !rescheduleDate) return;
    setSubmittingReschedule(true);
    try {
      await axios.put(`${API}/customer/jobs/${rescheduleJob.job_id}/reschedule`, {
        preferred_date: rescheduleDate,
        preferred_time: rescheduleTime,
      }, { withCredentials: true });
      toast.success("Reschedule request submitted! Pending admin approval.");
      setRescheduleJob(null);
      setRescheduleDate("");
      setRescheduleTime("morning");
      // Refresh data
      const [jobsRes, reqsRes] = await Promise.all([
        axios.get(`${API}/my-jobs`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/cut-requests`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setJobs(jobsRes.data || []);
      setMyRequests(reqsRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reschedule");
    } finally {
      setSubmittingReschedule(false);
    }
  };

  // Get pending cut requests for a specific date
  const getRequestsForDate = (date) => {
    return myRequests.filter(req => {
      try {
        return req.preferred_date && isSameDay(parseISO(req.preferred_date), date);
      } catch { return false; }
    });
  };

  // Dates with pending requests (for calendar markers)
  const pendingRequestDates = myRequests
    .filter(r => r.status === "pending" || r.status === "admin_edited")
    .map(r => { try { return parseISO(r.preferred_date); } catch { return null; } })
    .filter(Boolean);

  const openProfileEditor = () => {
    setProfileData({
      name: myCustomer?.name || user?.name || "",
      phone: myCustomer?.phone || "",
      address: myCustomer?.address || "",
      yard_size: myCustomer?.yard_size || "",
      grass_height: myCustomer?.grass_height || "",
    });
    setShowProfileEdit(true);
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      await axios.put(`${API}/customers/my-profile`, profileData, { withCredentials: true });
      toast.success("Profile updated!");
      setShowProfileEdit(false);
      const res = await axios.get(`${API}/customers/my-profile`, { withCredentials: true }).catch(() => ({ data: null }));
      setMyCustomer(res.data);
    } catch {
      toast.error("Failed to save profile.");
    } finally {
      setSavingProfile(false);
    }
  };

  const handleRespondToEdit = async (requestId, action) => {
    try {
      await axios.post(`${API}/cut-requests/${requestId}/respond`, { action }, { withCredentials: true });
      toast.success(action === "accept" ? "Changes accepted!" : "Changes rejected — original request kept");
      const res = await axios.get(`${API}/cut-requests`, { withCredentials: true });
      setMyRequests(res.data || []);
    } catch {
      toast.error("Failed to respond");
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), "EEEE, MMMM d, yyyy");
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "completed":
        return <span className="px-3 py-1 bg-[#39FF14]/20 text-[#39FF14] rounded-full text-xs">{t("dashboard.completed", "Completed")}</span>;
      case "in_progress":
        return <span className="px-3 py-1 bg-yellow-400/20 text-yellow-400 rounded-full text-xs">{t("dashboard.inProgress", "In Progress")}</span>;
      case "cancelled":
        return <span className="px-3 py-1 bg-red-400/20 text-red-400 rounded-full text-xs">{t("dashboard.cancelled", "Cancelled")}</span>;
      default:
        return <span className="px-3 py-1 bg-blue-400/20 text-blue-400 rounded-full text-xs">{t("dashboard.scheduled", "Scheduled")}</span>;
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Processing Payment Overlay */}
      {processingPayment && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center">
          <div className="bg-[#121212] border border-[#39FF14] rounded-xl p-8 text-center">
            <div className="w-12 h-12 border-4 border-[#39FF14] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-white font-medium">{t("portal.processingPayment", "Processing your payment...")}</p>
            <p className="text-[#A1A1AA] text-sm mt-2">{t("portal.pleaseWait", "Please wait while we confirm your subscription.")}</p>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-[#121212] border-b border-[#27272A]">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src={LOGO_URL} 
                alt="Zig Zag Lawncare" 
                className="h-12 w-auto object-contain rounded-lg"
              />
            </div>
            <div className="flex items-center gap-4">
              <div className="hidden sm:flex items-center gap-3">
                <Avatar className="w-8 h-8 border-2 border-[#39FF14]">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-[#064E3B] text-[#39FF14] text-sm">
                    {user?.name?.charAt(0) || "U"}
                  </AvatarFallback>
                </Avatar>
                <span className="text-white text-sm">{user?.name}</span>
              </div>
              <Button
                data-testid="portal-logout-btn"
                variant="outline"
                size="sm"
                className="border-[#27272A] text-[#A1A1AA] hover:bg-[#27272A] hover:text-white"
                onClick={logout}
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-white uppercase mb-2">
            {jobs.length > 0 || myQuote ? t("dashboard.welcomeBack", "Welcome back") : "Welcome"}, {user?.name?.split(" ")[0]}
          </h1>
          <p className="text-[#A1A1AA]">{t("portal.viewAppointments", "View your scheduled lawn care appointments")}</p>
        </div>

        {/* Two big actions: everything a customer usually needs */}
        <section className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8" data-testid="portal-main-actions">
          <button
            type="button"
            data-testid="portal-request-quote-btn"
            onClick={() => setShowBookingWizard(true)}
            className="group text-left bg-[#39FF14] text-black rounded-2xl p-6 transition-transform hover:-translate-y-0.5 hover:shadow-[0_0_30px_rgba(57,255,20,0.35)]"
          >
            <FileText className="w-8 h-8 mb-3" />
            <p className="font-heading text-2xl font-bold uppercase leading-tight">Request a Quote</p>
            <p className="text-black/70 text-sm mt-1">Tell us your address and yard, we send you a price</p>
            <ChevronRight className="w-6 h-6 mt-3 transition-transform group-hover:translate-x-1" />
          </button>
          <button
            type="button"
            data-testid="portal-request-cut-btn"
            onClick={openCutRequest}
            className="group text-left bg-[#FACC15] text-black rounded-2xl p-6 transition-transform hover:-translate-y-0.5 hover:shadow-[0_0_30px_rgba(250,204,21,0.35)]"
          >
            <Scissors className="w-8 h-8 mb-3" />
            <p className="font-heading text-2xl font-bold uppercase leading-tight">Request a Cut</p>
            <p className="text-black/70 text-sm mt-1">Pick a day and we'll come cut your grass</p>
            <ChevronRight className="w-6 h-6 mt-3 transition-transform group-hover:translate-x-1" />
          </button>
        </section>

        {/* My Info Card */}
        <section className="mb-8" data-testid="my-info-section">
          <div className="bg-[#121212] border border-[#27272A] rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg font-bold text-white uppercase flex items-center gap-2">
                <UserIcon className="w-5 h-5 text-[#39FF14]" />
                My Info
              </h2>
              <Button
                variant="outline"
                size="sm"
                className="border-[#39FF14]/50 text-[#39FF14] hover:bg-[#39FF14] hover:text-black"
                onClick={openProfileEditor}
                data-testid="edit-profile-btn"
              >
                <Pencil className="w-3.5 h-3.5 mr-1.5" />
                Edit
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-[#39FF14]/60 shrink-0" />
                <span className="text-[#A1A1AA]">Phone:</span>
                <span className={myCustomer?.phone ? "text-white" : "text-[#52525B] italic"}>{myCustomer?.phone || "Not set"}</span>
              </div>
              <div className="flex items-center gap-2">
                <Home className="w-4 h-4 text-[#39FF14]/60 shrink-0" />
                <span className="text-[#A1A1AA]">Address:</span>
                <span className={myCustomer?.address ? "text-white truncate" : "text-[#52525B] italic"}>{myCustomer?.address || "Not set"}</span>
              </div>
              <div className="flex items-center gap-2">
                <Leaf className="w-4 h-4 text-[#39FF14]/60 shrink-0" />
                <span className="text-[#A1A1AA]">Yard Size:</span>
                <span className={myCustomer?.yard_size ? "text-white capitalize" : "text-[#52525B] italic"}>{myCustomer?.yard_size || "Not set"}</span>
              </div>
              <div className="flex items-center gap-2">
                <Scissors className="w-4 h-4 text-[#39FF14]/60 shrink-0" />
                <span className="text-[#A1A1AA]">Grass Height:</span>
                <span className={myCustomer?.grass_height ? "text-white capitalize" : "text-[#52525B] italic"}>{myCustomer?.grass_height || "Not set"}</span>
              </div>
            </div>
            {(!myCustomer?.address || !myCustomer?.yard_size) && (
              <div className="mt-4 p-3 bg-[#39FF14]/5 border border-[#39FF14]/20 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-[#39FF14] shrink-0 mt-0.5" />
                <p className="text-[#A1A1AA] text-xs">Complete your profile so we can serve you better — tap <strong className="text-[#39FF14]">Edit</strong> to add your address and yard details.</p>
              </div>
            )}
          </div>
        </section>

        {/* Edit Profile Dialog */}
        <Dialog open={showProfileEdit} onOpenChange={setShowProfileEdit}>
          <DialogContent className="bg-[#121212] border-[#39FF14]/30 text-white max-w-md" data-testid="edit-profile-dialog">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <UserIcon className="w-5 h-5 text-[#39FF14]" />
                Edit Your Info
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="space-y-1.5">
                <Label className="text-[#A1A1AA] text-sm">Name</Label>
                <Input
                  value={profileData.name}
                  onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                  className="bg-[#0A0A0A] border-[#27272A] text-white"
                  data-testid="profile-name-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#A1A1AA] text-sm">Phone</Label>
                <Input
                  value={profileData.phone}
                  onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                  placeholder="e.g. 090-1234-5678"
                  className="bg-[#0A0A0A] border-[#27272A] text-white"
                  data-testid="profile-phone-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#A1A1AA] text-sm">Address</Label>
                <Input
                  value={profileData.address}
                  onChange={(e) => setProfileData({ ...profileData, address: e.target.value })}
                  placeholder="Your service address"
                  className="bg-[#0A0A0A] border-[#27272A] text-white"
                  data-testid="profile-address-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-[#A1A1AA] text-sm">Yard Size</Label>
                  <Select value={profileData.yard_size} onValueChange={(v) => setProfileData({ ...profileData, yard_size: v })}>
                    <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white" data-testid="profile-yard-size">
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-[#27272A]">
                      <SelectItem value="small" className="text-white">Small</SelectItem>
                      <SelectItem value="medium" className="text-white">Medium</SelectItem>
                      <SelectItem value="large" className="text-white">Large</SelectItem>
                      <SelectItem value="extra_large" className="text-white">Extra Large</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[#A1A1AA] text-sm">Grass Height</Label>
                  <Select value={profileData.grass_height} onValueChange={(v) => setProfileData({ ...profileData, grass_height: v })}>
                    <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white" data-testid="profile-grass-height">
                      <SelectValue placeholder="Select height" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-[#27272A]">
                      <SelectItem value="short" className="text-white">Short</SelectItem>
                      <SelectItem value="medium" className="text-white">Medium</SelectItem>
                      <SelectItem value="tall" className="text-white">Tall</SelectItem>
                      <SelectItem value="overgrown" className="text-white">Overgrown</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button
                onClick={handleSaveProfile}
                disabled={savingProfile}
                className="w-full bg-[#39FF14] text-black hover:bg-[#32D711] font-bold"
                data-testid="save-profile-btn"
              >
                {savingProfile ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Neighborhood Discount Claimed Banner */}
        {discountClaimed && (
          <section className="mb-8 animate-in fade-in slide-in-from-top-4 duration-500" data-testid="discount-claimed-banner">
            <div className="bg-gradient-to-r from-[#39FF14]/10 via-[#39FF14]/5 to-[#39FF14]/10 border-2 border-dashed border-[#39FF14]/50 rounded-xl p-6 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#39FF14]/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-xl bg-[#39FF14]/20 border border-[#39FF14]/40 flex items-center justify-center shrink-0">
                  <Scissors className="w-7 h-7 text-[#39FF14]" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#39FF14]/20 text-[#39FF14] border border-[#39FF14]/40">
                      SPECIAL OFFER
                    </span>
                  </div>
                  <h3 className="text-white font-bold text-lg">Neighborhood Discount</h3>
                  <p className="text-[#A1A1AA] text-sm mt-1 leading-relaxed">
                    You're eligible for a discount when we service multiple homes in your area.
                    Tap below to let us know — we'll apply the savings to your next booking!
                  </p>
                  <div className="flex gap-3 mt-4">
                    <Button
                      className="bg-[#39FF14] text-black hover:bg-[#32D711] font-bold"
                      onClick={handleClaimDiscount}
                      data-testid="claim-discount-send-btn"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Message Us to Claim
                    </Button>
                    <Button
                      variant="outline"
                      className="border-[#27272A] text-[#A1A1AA] hover:text-white"
                      onClick={() => setDiscountClaimed(false)}
                    >
                      Maybe Later
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Facebook Messenger CTA */}
        <section className="mb-8">
          <a
            href="https://m.me/zigzagokinawa"
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full bg-[#1877F2] hover:bg-[#166FE5] rounded-xl p-5 transition-all duration-300 hover:shadow-[0_0_30px_rgba(24,119,242,0.3)] group"
            data-testid="fb-messenger-cta"
          >
            <div className="flex items-center justify-center gap-3">
              <Facebook className="w-7 h-7 text-white" />
              <span className="font-heading text-lg md:text-xl font-bold text-white uppercase tracking-wider">
                Message Us on Facebook for the Fastest Response
              </span>
              <ChevronRight className="w-6 h-6 text-white group-hover:translate-x-1 transition-transform" />
            </div>
          </a>
        </section>

        {/* Current Subscription Status */}
        {subscription && (
          <section className="mb-8">
            <div className={`border-2 rounded-xl p-6 ${
              subscription.status === "paused" 
                ? "bg-yellow-400/10 border-yellow-400/50" 
                : "bg-[#39FF14]/10 border-[#39FF14]/50"
            }`}>
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                    subscription.status === "paused" ? "bg-yellow-400" : "bg-[#39FF14]"
                  }`}>
                    {subscription.status === "paused" ? (
                      <Pause className="w-7 h-7 text-black" />
                    ) : (
                      <Sparkles className="w-7 h-7 text-black" />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-heading text-xl font-bold text-white uppercase">
                        {subscription.plan === "yearly" ? t("landing.subscriptions.yearly", "Yearly") : t("landing.subscriptions.monthly", "Monthly")} {t("landing.subscriptions.title", "Subscription")}
                      </h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                        subscription.status === "paused" 
                          ? "bg-yellow-400 text-black" 
                          : "bg-[#39FF14] text-black"
                      }`}>
                        {subscription.status === "paused" ? t("portal.paused", "PAUSED") : t("portal.active", "ACTIVE")}
                      </span>
                    </div>
                    <p className="text-[#A1A1AA]">
                      {subscription.discount_percent}% {t("portal.offServices", "off all services")}
                      {subscription.status === "paused" && subscription.pause_until && (
                        <span className="text-yellow-400 ml-2">
                          • Paused until {format(parseISO(subscription.pause_until), "MMM d, yyyy")}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  {subscription.status === "active" ? (
                    <Button
                      onClick={handlePauseSubscription}
                      disabled={pausingSubscription}
                      variant="outline"
                      className="border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-black"
                    >
                      {pausingSubscription ? (
                        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                      ) : (
                        <Pause className="w-4 h-4 mr-2" />
                      )}
                      Pause (1 Month)
                    </Button>
                  ) : (
                    <Button
                      onClick={handleResumeSubscription}
                      disabled={pausingSubscription}
                      className="bg-[#39FF14] text-black hover:bg-[#32D711]"
                    >
                      {pausingSubscription ? (
                        <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2" />
                      ) : (
                        <Play className="w-4 h-4 mr-2" />
                      )}
                      Resume
                    </Button>
                  )}
                  <Button
                    onClick={handleCancelSubscription}
                    variant="ghost"
                    className="text-red-400 hover:bg-red-400/10"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              {subscription.status === "paused" && (
                <div className="mt-4 p-3 bg-yellow-400/10 rounded-lg flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-yellow-400 shrink-0 mt-0.5" />
                  <p className="text-yellow-400 text-sm">
                    {cms("subscription_status", "pause_warning", "Your subscription is paused. You won't be charged for scheduled cuts during this time. Resume anytime to continue your service.")}
                  </p>
                </div>
              )}
            </div>
          </section>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-12 h-12 border-4 border-[#39FF14] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <>
            {/* Schedule Calendar */}
            <section className="mb-8">
              <h2 className="font-heading text-xl font-bold text-white uppercase mb-4 flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-[#39FF14]" />
                {cms("schedule", "section_title", "Your Schedule")}
              </h2>
              
              <div className="bg-[#121212] border border-[#27272A] rounded-xl p-4 md:p-6">
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Calendar */}
                  <div className="flex justify-center">
                    <Calendar
                      mode="single"
                      selected={selectedCalendarDate}
                      onSelect={(date) => { setSelectedCalendarDate(date); setShowQuickSchedule(false); }}
                      month={calendarMonth}
                      onMonthChange={setCalendarMonth}
                      modifiers={{
                        scheduled: scheduledDates,
                        pending: pendingRequestDates,
                      }}
                      modifiersStyles={{
                        scheduled: {
                          backgroundColor: "rgba(57, 255, 20, 0.2)",
                          color: "#39FF14",
                          fontWeight: "bold",
                          borderRadius: "50%",
                        },
                        pending: {
                          backgroundColor: "rgba(234, 179, 8, 0.2)",
                          color: "#EAB308",
                          fontWeight: "bold",
                          borderRadius: "50%",
                        }
                      }}
                      className="rounded-md border border-[#27272A] bg-[#0A0A0A]"
                      classNames={{
                        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                        month: "space-y-4",
                        caption: "flex justify-center pt-1 relative items-center text-white",
                        caption_label: "text-sm font-medium",
                        nav: "space-x-1 flex items-center",
                        nav_button: "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 text-white border border-[#27272A] rounded-md hover:bg-[#27272A]",
                        nav_button_previous: "absolute left-1",
                        nav_button_next: "absolute right-1",
                        table: "w-full border-collapse space-y-1",
                        head_row: "flex",
                        head_cell: "text-[#A1A1AA] rounded-md w-9 font-normal text-[0.8rem]",
                        row: "flex w-full mt-2",
                        cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 h-9 w-9",
                        day: "h-9 w-9 p-0 font-normal text-white hover:bg-[#27272A] rounded-md transition-colors",
                        day_selected: "bg-[#39FF14] text-black hover:bg-[#32D711] hover:text-black",
                        day_today: "bg-[#27272A] text-white",
                        day_outside: "text-[#A1A1AA] opacity-50",
                        day_disabled: "text-[#A1A1AA] opacity-50",
                      }}
                    />
                  </div>

                  {/* Selected Date Details */}
                  <div>
                    <h3 className="font-medium text-white mb-3">
                      {selectedCalendarDate 
                        ? format(selectedCalendarDate, "EEEE, MMMM d, yyyy")
                        : "Select a date to view details"
                      }
                    </h3>
                    
                    {selectedCalendarDate && (
                      <div className="space-y-3">
                        {getJobsForDate(selectedCalendarDate).length > 0 ? (
                          getJobsForDate(selectedCalendarDate).map(job => (
                            <div 
                              key={job.job_id}
                              className="bg-[#0A0A0A] border border-[#39FF14]/30 rounded-lg p-4"
                            >
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <Scissors className="w-4 h-4 text-[#39FF14]" />
                                  <span className="text-white font-medium">Grass Cutting</span>
                                </div>
                                {getStatusBadge(job.status)}
                              </div>
                              <div className="flex items-center gap-2 text-[#A1A1AA] text-sm mb-2">
                                <Clock className="w-4 h-4" />
                                <span>{job.scheduled_time}</span>
                                <span className="text-[#27272A]">•</span>
                                <span>{job.duration_minutes} min</span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-[#39FF14] font-bold">${job.final_price?.toFixed(2)}</span>
                                <div className="flex gap-2">
                                  {job.status === "scheduled" && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="border-yellow-400/50 text-yellow-400 hover:bg-yellow-400/10"
                                      onClick={() => { setRescheduleJob(job); setRescheduleDate(""); setRescheduleTime("morning"); }}
                                      data-testid={`reschedule-job-${job.job_id}`}
                                    >
                                      <Edit3 className="w-3 h-3 mr-1" />
                                      Edit
                                    </Button>
                                  )}
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14] hover:text-black"
                                    onClick={() => openCalendarModal(job)}
                                  >
                                    <CalendarPlus className="w-3 h-3 mr-1" />
                                    Add to Calendar
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))
                        ) : getRequestsForDate(selectedCalendarDate).length > 0 ? (
                          getRequestsForDate(selectedCalendarDate).map(req => (
                            <div key={req.request_id} className="bg-[#0A0A0A] border border-yellow-500/30 rounded-lg p-4">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <Scissors className="w-4 h-4 text-yellow-400" />
                                  <span className="text-white font-medium">Scheduled Cut</span>
                                </div>
                                <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-yellow-500/20 text-yellow-400">
                                  {req.status === "admin_edited" ? "Review Changes" : "Pending Approval"}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-[#A1A1AA] text-sm">
                                <Clock className="w-4 h-4" />
                                <span className="capitalize">{req.preferred_time}</span>
                              </div>
                              {req.notes && <p className="text-[#A1A1AA] text-xs mt-1">{req.notes}</p>}
                            </div>
                          ))
                        ) : (
                          <div className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-4 text-center">
                            <CalendarIcon className="w-8 h-8 text-[#27272A] mx-auto mb-2" />
                            <p className="text-[#A1A1AA] text-sm">No appointments on this date</p>
                          </div>
                        )}

                        {/* Schedule a Cut button for subscribers */}
                        {subscription && subscription.status !== "paused" && !showQuickSchedule && (
                          <Button
                            size="sm"
                            className="w-full bg-[#39FF14] text-black hover:bg-[#32D711] font-bold mt-2"
                            onClick={() => setShowQuickSchedule(true)}
                            data-testid="quick-schedule-btn"
                          >
                            <CalendarPlus className="w-4 h-4 mr-2" />
                            Schedule a Cut
                          </Button>
                        )}

                        {/* Quick schedule form */}
                        {showQuickSchedule && (
                          <div className="bg-[#121212] border border-[#39FF14]/30 rounded-lg p-4 space-y-3 mt-2" data-testid="quick-schedule-form">
                            <p className="text-white text-sm font-medium">Schedule for {format(selectedCalendarDate, "MMM d, yyyy")}</p>
                            <div>
                              <Label className="text-[#A1A1AA] text-xs">Preferred Time</Label>
                              <Select value={quickScheduleTime} onValueChange={setQuickScheduleTime}>
                                <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white mt-1">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-[#121212] border-[#27272A]">
                                  <SelectItem value="morning" className="text-white hover:bg-[#27272A]">Morning (8am-12pm)</SelectItem>
                                  <SelectItem value="afternoon" className="text-white hover:bg-[#27272A]">Afternoon (12pm-4pm)</SelectItem>
                                  <SelectItem value="evening" className="text-white hover:bg-[#27272A]">Evening (4pm-7pm)</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-[#A1A1AA] text-xs">Notes (optional)</Label>
                              <Input
                                value={quickScheduleNotes}
                                onChange={e => setQuickScheduleNotes(e.target.value)}
                                placeholder="Any special requests..."
                                className="bg-[#0A0A0A] border-[#27272A] text-white mt-1"
                              />
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                className="flex-1 border-[#27272A] text-[#A1A1AA]"
                                onClick={() => setShowQuickSchedule(false)}
                              >
                                Cancel
                              </Button>
                              <Button
                                size="sm"
                                className="flex-1 bg-[#39FF14] text-black hover:bg-[#32D711] font-bold"
                                onClick={handleQuickSchedule}
                                disabled={submittingQuickSchedule}
                                data-testid="confirm-quick-schedule-btn"
                              >
                                {submittingQuickSchedule ? "Submitting..." : "Confirm"}
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {!selectedCalendarDate && scheduledDates.length > 0 && (
                      <div className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-4">
                        <p className="text-[#A1A1AA] text-sm mb-2">You have {scheduledDates.length} scheduled cut{scheduledDates.length > 1 ? 's' : ''}:</p>
                        <div className="space-y-1">
                          {scheduledDates.slice(0, 3).map((date, i) => (
                            <button
                              key={i}
                              onClick={() => setSelectedCalendarDate(date)}
                              className="flex items-center gap-2 text-[#39FF14] text-sm hover:underline w-full text-left"
                            >
                              <Scissors className="w-3 h-3" />
                              {format(date, "MMM d, yyyy")}
                            </button>
                          ))}
                          {scheduledDates.length > 3 && (
                            <p className="text-[#A1A1AA] text-xs">+{scheduledDates.length - 3} more</p>
                          )}
                        </div>
                      </div>
                    )}

                    {!selectedCalendarDate && scheduledDates.length === 0 && (
                      <div className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-4 text-center">
                        <CalendarIcon className="w-8 h-8 text-[#27272A] mx-auto mb-2" />
                        <p className="text-[#A1A1AA] text-sm">No scheduled appointments</p>
                        {subscription ? (
                          <p className="text-[#A1A1AA] text-xs mt-1">Select a date to schedule a cut</p>
                        ) : (
                          <Button
                            size="sm"
                            className="mt-3 bg-[#39FF14] text-black hover:bg-[#32D711]"
                            onClick={() => setShowBookingWizard(true)}
                          >
                            Get a Quote
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Legend */}
                <div className="flex items-center gap-4 mt-4 pt-4 border-t border-[#27272A]">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-[#39FF14]/20 border border-[#39FF14]"></div>
                    <span className="text-[#A1A1AA] text-xs">Scheduled Cut</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-yellow-500/20 border border-yellow-500"></div>
                    <span className="text-[#A1A1AA] text-xs">Pending Approval</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-[#27272A]"></div>
                    <span className="text-[#A1A1AA] text-xs">Today</span>
                  </div>
                </div>
              </div>
            </section>

            {/* Request a Grass Cut */}
            <section className="mb-8" data-testid="cut-request-section">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-xl font-bold text-white uppercase flex items-center gap-2">
                  <Scissors className="w-5 h-5 text-[#39FF14]" />
                  {cms("cut_request", "section_title", "Request a Grass Cut")}
                </h2>
                <Button
                  size="sm"
                  onClick={() => (showCutRequest ? setShowCutRequest(false) : openCutRequest())}
                  className="bg-[#39FF14] text-black hover:bg-[#32D711] font-bold"
                  data-testid="request-cut-btn"
                >
                  {showCutRequest ? cms("cut_request", "cancel_button", "Cancel") : cms("cut_request", "new_request_button", "+ New Request")}
                </Button>
              </div>

              {/* Disclaimer */}
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mb-4">
                <p className="text-yellow-400 text-xs flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>
                    {cms("cut_request", "disclaimer", "Not all requests will be fulfilled. Each cut request is reviewed and approved on a case-by-case basis. Pricing is subject to review based on yard size, grass condition, and location.")}
                  </span>
                </p>
              </div>

              {/* Request Form */}
              {showCutRequest && (
                <div className="bg-[#121212] border border-[#39FF14]/30 rounded-xl p-6 mb-4 space-y-4" data-testid="cut-request-form">
                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA] text-sm">Address <span className="text-[#39FF14]">*</span></Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#39FF14]" />
                      <Input
                        value={cutRequestData.address}
                        onChange={(e) => setCutRequestData({ ...cutRequestData, address: e.target.value })}
                        placeholder="e.g. 3725 Camp Foster"
                        className="pl-9 bg-[#0A0A0A] border-[#27272A] text-white"
                        data-testid="cut-request-address"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-[#A1A1AA] text-sm">{cms("cut_request", "preferred_date_label", "Preferred Date")}</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={`w-full justify-start text-left font-normal bg-[#0A0A0A] border-[#27272A] hover:bg-[#1a1a1a] ${cutRequestData.preferred_date ? "text-white" : "text-[#71717A]"}`}
                            data-testid="cut-request-date"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4 text-[#39FF14]" />
                            {cutRequestData.preferred_date
                              ? format(new Date(cutRequestData.preferred_date + "T00:00:00"), "MMMM d, yyyy")
                              : "Pick a date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 bg-[#121212] border-[#39FF14]/30" align="start">
                          <Calendar
                            mode="single"
                            selected={cutRequestData.preferred_date ? new Date(cutRequestData.preferred_date + "T00:00:00") : undefined}
                            onSelect={(date) => {
                              if (date) {
                                const yyyy = date.getFullYear();
                                const mm = String(date.getMonth() + 1).padStart(2, "0");
                                const dd = String(date.getDate()).padStart(2, "0");
                                setCutRequestData({ ...cutRequestData, preferred_date: `${yyyy}-${mm}-${dd}` });
                              }
                            }}
                            defaultMonth={new Date()}
                            disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                            className="text-white"
                            classNames={{
                              day_selected: "bg-[#39FF14] text-black hover:bg-[#32D711] focus:bg-[#32D711]",
                              day_today: "bg-[#39FF14]/20 text-[#39FF14]",
                              caption_label: "text-white text-sm font-medium",
                              nav_button: "text-[#39FF14] hover:bg-[#39FF14]/20 h-7 w-7 bg-transparent p-0",
                              head_cell: "text-[#A1A1AA] rounded-md w-8 font-normal text-[0.8rem]",
                              cell: "text-white h-8 w-8 text-center text-sm p-0 relative focus-within:relative focus-within:z-20",
                              day: "h-8 w-8 p-0 font-normal text-white hover:bg-[#39FF14]/20 rounded-md",
                              day_outside: "text-[#52525B] opacity-50",
                              day_disabled: "text-[#52525B] opacity-30",
                            }}
                            data-testid="cut-request-calendar"
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[#A1A1AA] text-sm">{cms("cut_request", "preferred_time_label", "Preferred Time")}</Label>
                      <Select value={cutRequestData.preferred_time} onValueChange={(v) => setCutRequestData({ ...cutRequestData, preferred_time: v })}>
                        <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white" data-testid="cut-request-time">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#121212] border-[#27272A]">
                          <SelectItem value="morning" className="text-white">{cms("cut_request", "time_morning", "Morning (8am - 12pm)")}</SelectItem>
                          <SelectItem value="afternoon" className="text-white">{cms("cut_request", "time_afternoon", "Afternoon (12pm - 4pm)")}</SelectItem>
                          <SelectItem value="evening" className="text-white">{cms("cut_request", "time_evening", "Evening (4pm - 7pm)")}</SelectItem>
                          <SelectItem value="anytime" className="text-white">{cms("cut_request", "time_anytime", "Anytime")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${cutRequestData.returning_customer ? "bg-[#39FF14]/10 border-[#39FF14]/50" : "bg-[#0A0A0A] border-[#27272A] hover:border-[#39FF14]/30"}`}
                    onClick={() => setCutRequestData({ ...cutRequestData, returning_customer: !cutRequestData.returning_customer, estimated_price: !cutRequestData.returning_customer ? "" : cutRequestData.estimated_price })}
                    data-testid="returning-customer-toggle"
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${cutRequestData.returning_customer ? "bg-[#39FF14] border-[#39FF14]" : "border-[#52525B]"}`}>
                      {cutRequestData.returning_customer && <CheckCircle className="w-3.5 h-3.5 text-black" />}
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">Returning Customer</p>
                      <p className="text-[#A1A1AA] text-xs">I already have a quote — use my existing price</p>
                    </div>
                  </div>
                  <div className={`space-y-2 transition-opacity ${cutRequestData.returning_customer ? "opacity-40 pointer-events-none" : "opacity-100"}`}>
                    <Label className="text-[#A1A1AA] text-sm">{cms("cut_request", "estimated_price_label", "Your Estimated Price ($)")}</Label>
                    <p className="text-[#A1A1AA] text-xs">{cms("cut_request", "estimated_price_desc", "How much are you willing to pay for this cut?")}</p>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#39FF14]" />
                      <Input
                        type="number"
                        placeholder="e.g. 75.00"
                        value={cutRequestData.estimated_price}
                        onChange={(e) => setCutRequestData({ ...cutRequestData, estimated_price: e.target.value })}
                        className="pl-9 bg-[#0A0A0A] border-[#27272A] text-white"
                        data-testid="cut-request-price"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#A1A1AA] text-sm">{cms("cut_request", "notes_label", "Additional Notes (optional)")}</Label>
                    <Textarea
                      placeholder={cms("cut_request", "notes_placeholder", "Anything we should know? Special requests, gate codes, etc.")}
                      value={cutRequestData.notes}
                      onChange={(e) => setCutRequestData({ ...cutRequestData, notes: e.target.value })}
                      className="bg-[#0A0A0A] border-[#27272A] text-white h-20"
                      data-testid="cut-request-notes"
                    />
                  </div>
                  <Button
                    onClick={handleSubmitCutRequest}
                    disabled={submittingCutRequest || !(cutRequestData.address || "").trim() || !cutRequestData.preferred_date || (!cutRequestData.returning_customer && !cutRequestData.estimated_price)}
                    className="w-full bg-[#39FF14] text-black hover:bg-[#32D711] font-bold"
                    data-testid="submit-cut-request-btn"
                  >
                    {submittingCutRequest ? "Submitting..." : cms("cut_request", "submit_button", "Submit Cut Request")}
                  </Button>
                </div>
              )}

              {/* Previous Requests */}
              {myRequests.length > 0 && (
                <div className="space-y-3">
                  <p className="text-[#A1A1AA] text-xs uppercase font-semibold">{cms("cut_request", "your_requests_label", "Your Requests")}</p>
                  {myRequests.map((req) => (
                    <div
                      key={req.request_id}
                      className={`bg-[#121212] border rounded-lg p-4 ${req.status === "admin_edited" ? "border-orange-500/40" : "border-[#27272A]"}`}
                      data-testid={`cut-request-${req.request_id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white text-sm font-medium">
                            {req.preferred_date ? format(parseISO(req.preferred_date), "MMM d, yyyy") : "No date"}
                            <span className="text-[#A1A1AA] text-xs ml-2 capitalize">{req.preferred_time}</span>
                          </p>
                          {!req.returning_customer && (
                            <p className="text-[#39FF14] text-sm font-bold mt-1">
                              ${parseFloat(req.estimated_price || 0).toFixed(2)} estimated
                            </p>
                          )}
                          {req.notes && <p className="text-[#A1A1AA] text-xs mt-1">{req.notes}</p>}
                          {req.admin_notes && req.status !== "admin_edited" && (
                            <p className="text-yellow-400 text-xs mt-1">Admin: {req.admin_notes}</p>
                          )}
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-bold shrink-0 ${
                          req.status === "approved" ? "bg-[#39FF14]/20 text-[#39FF14]" :
                          req.status === "declined" ? "bg-red-500/20 text-red-400" :
                          req.status === "admin_edited" ? "bg-orange-500/20 text-orange-400" :
                          "bg-yellow-500/20 text-yellow-400"
                        }`}>
                          {req.status === "admin_edited" ? "Review Changes" : req.status}
                        </span>
                      </div>

                      {/* Admin Edit Proposal */}
                      {req.status === "admin_edited" && req.proposed_changes && (
                        <div className="mt-3 p-3 bg-orange-500/5 border border-orange-500/20 rounded-lg space-y-2">
                          <div className="flex items-center gap-2">
                            <Timer className="w-4 h-4 text-orange-400" />
                            <p className="text-orange-400 text-sm font-medium">Admin proposed changes</p>
                          </div>
                          <div className="space-y-1 text-xs">
                            {req.proposed_changes.preferred_date && (
                              <p className="text-white">
                                Date: <span className="line-through text-[#52525B]">{req.original_values?.preferred_date}</span> → <span className="text-orange-400 font-medium">{format(parseISO(req.proposed_changes.preferred_date), "MMM d, yyyy")}</span>
                              </p>
                            )}
                            {req.proposed_changes.preferred_time && (
                              <p className="text-white">
                                Time: <span className="line-through text-[#52525B] capitalize">{req.original_values?.preferred_time}</span> → <span className="text-orange-400 font-medium capitalize">{req.proposed_changes.preferred_time}</span>
                              </p>
                            )}
                            {req.proposed_changes.estimated_price != null && (
                              <p className="text-white">
                                Price: <span className="line-through text-[#52525B]">${parseFloat(req.original_values?.estimated_price || 0).toFixed(2)}</span> → <span className="text-orange-400 font-medium">${parseFloat(req.proposed_changes.estimated_price).toFixed(2)}</span>
                              </p>
                            )}
                          </div>
                          {req.admin_notes && (
                            <p className="text-[#A1A1AA] text-xs">Note: {req.admin_notes}</p>
                          )}
                          <p className="text-[#52525B] text-xs">
                            Expires {req.edit_expires_at ? format(parseISO(req.edit_expires_at), "MMM d 'at' h:mm a") : "in 24 hours"}
                          </p>
                          <div className="flex gap-2 pt-1">
                            <Button
                              size="sm"
                              className="flex-1 bg-[#39FF14] text-black hover:bg-[#32D711] font-bold text-xs"
                              onClick={() => handleRespondToEdit(req.request_id, "accept")}
                              data-testid={`accept-edit-${req.request_id}`}
                            >
                              <CheckCircle className="w-3.5 h-3.5 mr-1" />
                              Accept Changes
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/10 text-xs"
                              onClick={() => handleRespondToEdit(req.request_id, "reject")}
                              data-testid={`reject-edit-${req.request_id}`}
                            >
                              <X className="w-3.5 h-3.5 mr-1" />
                              Reject
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Upcoming Appointments */}
            <section className="mb-8">
              <h2 className="font-heading text-xl font-bold text-white uppercase mb-4 flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-[#39FF14]" />
                {cms("appointments", "upcoming_title", "Upcoming Appointments")}
              </h2>
              
              {upcomingJobs.length === 0 ? (
                <div className="bg-[#121212] border border-[#27272A] rounded-xl p-8 text-center">
                  <CalendarIcon className="w-16 h-16 text-[#27272A] mx-auto mb-4" />
                  <p className="text-[#A1A1AA]">{cms("appointments", "no_upcoming", "No upcoming appointments")}</p>
                  <p className="text-[#A1A1AA] text-sm mt-2">{cms("appointments", "no_upcoming_desc", "Contact us to schedule your next lawn service")}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {upcomingJobs.map((job) => (
                    <div 
                      key={job.job_id}
                      data-testid={`portal-job-${job.job_id}`}
                      className="bg-[#121212] border border-[#27272A] rounded-xl p-6 hover:border-[#39FF14]/30 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-white font-semibold text-lg">{formatDate(job.scheduled_date)}</h3>
                          <div className="flex items-center gap-2 text-[#A1A1AA] mt-1">
                            <Clock className="w-4 h-4" />
                            <span>{job.scheduled_time}</span>
                            <span className="text-[#27272A]">•</span>
                            <span>{job.duration_minutes} minutes</span>
                          </div>
                        </div>
                        {getStatusBadge(job.status)}
                      </div>
                      <div className="flex items-start gap-2 text-[#A1A1AA] mb-4">
                        <MapPin className="w-4 h-4 mt-0.5" />
                        <span>{job.customer_address}</span>
                      </div>
                      <div className="flex items-center justify-between pt-4 border-t border-[#27272A]">
                        <div className="flex items-center gap-2">
                          {job.discount_applied > 0 && (
                            <span className="text-[#A1A1AA] text-sm line-through">${(job.price || 0).toFixed(2)}</span>
                          )}
                          <span className="text-[#39FF14] font-bold text-lg flex items-center gap-1">
                            <DollarSign className="w-4 h-4" />
                            {(job.final_price || 0).toFixed(2)}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          {job.discount_applied > 0 && (
                            <span className="text-xs text-[#39FF14] bg-[#39FF14]/10 px-3 py-1 rounded-full">
                              Subscription discount applied!
                            </span>
                          )}
                          {job.status === "scheduled" && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-yellow-400/50 text-yellow-400 hover:bg-yellow-400/10"
                              onClick={() => { setRescheduleJob(job); setRescheduleDate(""); setRescheduleTime("morning"); }}
                              data-testid={`reschedule-upcoming-${job.job_id}`}
                            >
                              <Edit3 className="w-4 h-4 mr-2" />
                              Edit
                            </Button>
                          )}
                          <Button
                            data-testid={`add-to-calendar-${job.job_id}`}
                            variant="outline"
                            size="sm"
                            className="border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14] hover:text-black"
                            onClick={() => openCalendarModal(job)}
                          >
                            <CalendarPlus className="w-4 h-4 mr-2" />
                            Add to Calendar
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Past Appointments */}
            {pastJobs.length > 0 && (
              <section className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading text-xl font-bold text-white uppercase flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-[#39FF14]" />
                    {cms("appointments", "past_title", "Past Appointments")}
                  </h2>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-[#39FF14]/50 text-[#39FF14] hover:bg-[#39FF14]/10"
                    onClick={async () => {
                      try {
                        const year = new Date().getFullYear();
                        const res = await axios.get(`${API}/customer/my-receipt?year=${year}`, { withCredentials: true });
                        const data = res.data;
                        if (data.jobs.length === 0) { toast.error(`No completed jobs in ${year}`); return; }
                        const win = window.open("", "_blank");
                        win.document.write(`<html><head><title>Receipt - ${data.customer.name} - ${year}</title><style>
                          *{margin:0;padding:0;box-sizing:border-box}
                          body{font-family:Arial,sans-serif;padding:40px;color:#222;max-width:800px;margin:0 auto}
                          .header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:30px;padding-bottom:20px;border-bottom:3px solid #39FF14}
                          .company h1{font-size:24px;color:#0A0A0A;margin-bottom:4px}
                          .company p{color:#666;font-size:12px}
                          .receipt-info{text-align:right}
                          .receipt-info h2{font-size:28px;color:#39FF14;text-transform:uppercase;letter-spacing:2px}
                          .receipt-info p{color:#666;font-size:13px;margin-top:4px}
                          .customer-info{background:#f8f8f8;border-radius:8px;padding:16px;margin-bottom:24px}
                          .customer-info h3{font-size:11px;text-transform:uppercase;letter-spacing:1px;color:#999;margin-bottom:8px}
                          .customer-info p{font-size:14px;margin-bottom:2px}
                          table{width:100%;border-collapse:collapse;margin-bottom:24px}
                          th{background:#0A0A0A;color:white;text-align:left;padding:10px 14px;font-size:11px;text-transform:uppercase;letter-spacing:.5px}
                          th:last-child{text-align:right}
                          td{padding:10px 14px;border-bottom:1px solid #eee;font-size:13px}
                          td:last-child{text-align:right;font-weight:bold}
                          tr:nth-child(even){background:#fafafa}
                          .totals{display:flex;justify-content:flex-end;margin-bottom:30px}
                          .totals-box{background:#0A0A0A;color:white;border-radius:8px;padding:16px 24px;min-width:250px}
                          .totals-row{display:flex;justify-content:space-between;padding:4px 0;font-size:14px}
                          .totals-row.total{border-top:1px solid #333;padding-top:10px;margin-top:8px;font-size:18px;font-weight:bold}
                          .totals-row.total .amount{color:#39FF14}
                          .footer{text-align:center;color:#999;font-size:11px;padding-top:20px;border-top:1px solid #eee}
                          @media print{body{padding:20px}}
                        </style></head><body>
                          <div class="header"><div class="company"><h1>Zig Zag Lawncare Okinawa</h1><p>Professional Lawn Care Services</p><p>zigzaglawn@gmail.com | m.me/zigzagokinawa</p></div>
                          <div class="receipt-info"><h2>Receipt</h2><p>Year: ${year}</p><p>Generated: ${new Date().toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}</p></div></div>
                          <div class="customer-info"><h3>Bill To</h3><p><strong>${data.customer.name}</strong></p>${data.customer.address?`<p>${data.customer.address}</p>`:""}${data.customer.email?`<p>${data.customer.email}</p>`:""}${data.customer.phone?`<p>${data.customer.phone}</p>`:""}</div>
                          <table><thead><tr><th>#</th><th>Date</th><th>Description</th><th>Amount</th></tr></thead><tbody>
                          ${data.jobs.map((j,i)=>`<tr><td>${i+1}</td><td>${new Date(j.date+"T12:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}</td><td>${j.description||"Lawn Care Service"}${j.duration?` (${j.duration} min)`:""}</td><td>$${Number(j.price).toFixed(2)}</td></tr>`).join("")}
                          </tbody></table>
                          <div class="totals"><div class="totals-box"><div class="totals-row"><span>Services Completed</span><span>${data.job_count}</span></div><div class="totals-row total"><span>Total Paid</span><span class="amount">$${Number(data.total).toFixed(2)}</span></div></div></div>
                          <div class="footer"><p>Thank you for choosing Zig Zag Lawncare Okinawa!</p><p>This receipt is for your records. No payment is due.</p></div>
                        </body></html>`);
                        win.document.close();
                        win.print();
                      } catch (e) { toast.error("Failed to generate receipt"); }
                    }}
                    data-testid="customer-receipt-btn"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    {new Date().getFullYear()} Receipt
                  </Button>
                </div>
                <div className="space-y-3">
                  {pastJobs.map((job) => (
                    <div 
                      key={job.job_id}
                      className="bg-[#121212] border border-[#27272A] rounded-xl p-4 flex items-center justify-between"
                    >
                      <div>
                        <p className="text-white">{formatDate(job.scheduled_date)}</p>
                        <p className="text-[#A1A1AA] text-sm">{job.customer_address}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-[#39FF14] font-bold">${(job.final_price || 0).toFixed(2)}</span>
                        {getStatusBadge(job.status)}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}

        {/* Subscription Plans - Show if no active subscription AND subscriptions are enabled */}
        {(!subscription || subscription?.plan === "monthly") && subscriptionsEnabled && (
          <section className="mt-8">
            <div className="text-center mb-6">
              <h2 className="font-heading text-2xl font-bold text-white uppercase mb-2">
                {subscription?.plan === "monthly" 
                  ? cms("subscription_plans", "upgrade_title", "Upgrade & Save More!")
                  : cms("subscription_plans", "title", "Save with Subscriptions")}
              </h2>
              <p className="text-[#A1A1AA]">
                {subscription?.plan === "monthly"
                  ? "Switch to yearly and save even more on every cut!"
                  : myQuote ? cms("subscription_plans", "subtitle_with_quote", "Your personalized subscription pricing") : cms("subscription_plans", "subtitle_no_quote", "Choose a plan and save on every service")}
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              {/* Monthly Plan */}
              <div className={`bg-[#121212] border rounded-xl p-6 transition-all ${subscription?.plan === "monthly" ? "border-[#39FF14] opacity-75" : "border-[#27272A] hover:border-[#39FF14]/50"}`}>
                {subscription?.plan === "monthly" && (
                  <div className="mb-3 text-center">
                    <span className="bg-[#39FF14] text-black text-xs font-bold px-3 py-1 rounded-full">YOUR CURRENT PLAN</span>
                  </div>
                )}
                <div className="text-center mb-4">
                  <h3 className="font-heading text-xl font-bold text-white uppercase">{cms("subscription_plans", "monthly_title", "Monthly")}</h3>
                  <div className="flex items-center justify-center gap-2 mt-2">
                    <span className="text-4xl font-bold text-[#39FF14]">{plans.monthly?.discount_percent || 10}%</span>
                    <span className="text-[#A1A1AA]">OFF</span>
                  </div>
                </div>
                {/* Frequency Tiers */}
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <div className="text-center p-2.5 rounded-lg border border-[#39FF14]/30 bg-[#39FF14]/5">
                    <p className="font-bold text-white text-sm">1x / month</p>
                    <p className="text-[#A1A1AA] text-xs">One cut per month</p>
                    {myQuote?.monthly_1x_price && (
                      <p className="text-[#39FF14] text-sm font-bold mt-1">${myQuote.monthly_1x_price.toFixed(2)}/mo</p>
                    )}
                  </div>
                  <div className="text-center p-2.5 rounded-lg border border-[#39FF14]/30 bg-[#39FF14]/5">
                    <p className="font-bold text-white text-sm">2x / month</p>
                    <p className="text-[#A1A1AA] text-xs">Two cuts per month</p>
                    {myQuote?.monthly_2x_price && (
                      <p className="text-[#39FF14] text-sm font-bold mt-1">${myQuote.monthly_2x_price.toFixed(2)}/mo</p>
                    )}
                  </div>
                </div>
                {!(myQuote?.monthly_1x_price || myQuote?.monthly_2x_price) && (
                  <p className="text-[#A1A1AA] text-sm text-center mb-4">Contact us for pricing</p>
                )}
                <ul className="space-y-2 mb-6">
                  {plans.monthly?.features?.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-[#A1A1AA] text-sm">
                      <CheckCircle className="w-4 h-4 text-[#39FF14]" />
                      {feature}
                    </li>
                  ))}
                  <li className="flex items-center gap-2 text-yellow-400 text-sm">
                    <Pause className="w-4 h-4" />
                    Pause anytime (1 month max)
                  </li>
                </ul>
                <Button
                  onClick={() => handleSubscribe("monthly")}
                  disabled={subscription?.plan === "monthly" || requestingSubscription || processingPayment}
                  className={`w-full font-bold disabled:opacity-50 disabled:cursor-not-allowed ${
                    subscription?.plan === "monthly" 
                      ? "bg-[#27272A] text-[#A1A1AA] cursor-default" 
                      : "bg-[#39FF14] text-black hover:bg-[#32D711]"
                  }`}
                >
                  {subscription?.plan === "monthly" ? (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Currently Subscribed
                    </>
                  ) : requestingSubscription || processingPayment ? (
                    <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2" />
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      Subscribe Monthly
                    </>
                  )}
                </Button>
              </div>

              {/* Yearly Plan */}
              <div className={`bg-gradient-to-b from-[#064E3B] to-[#121212] border-2 rounded-xl p-6 relative ${
                subscription?.plan === "monthly" ? "border-[#39FF14] shadow-[0_0_20px_rgba(57,255,20,0.2)] ring-2 ring-[#39FF14]/30" : "border-[#39FF14]"
              }`}>
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-[#39FF14] text-black text-xs font-bold px-3 py-1 rounded-full">
                    {subscription?.plan === "monthly" ? "UPGRADE & SAVE MORE" : cms("subscription_plans", "best_value_badge", "BEST VALUE")}
                  </span>
                </div>
                <div className="text-center mb-4">
                  <h3 className="font-heading text-xl font-bold text-white uppercase">{cms("subscription_plans", "yearly_title", "Yearly")}</h3>
                  <div className="flex items-center justify-center gap-2 mt-2">
                    <span className="text-4xl font-bold text-[#39FF14]">{plans.yearly?.discount_percent || 25}%</span>
                    <span className="text-[#A1A1AA]">OFF</span>
                  </div>
                </div>
                {/* Frequency Tiers */}
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <div className="text-center p-2.5 rounded-lg border border-[#39FF14]/30 bg-[#39FF14]/5">
                    <p className="font-bold text-white text-sm">1x / month</p>
                    <p className="text-[#A1A1AA] text-xs">One cut per month</p>
                    {myQuote?.yearly_1x_price && (
                      <p className="text-[#39FF14] text-sm font-bold mt-1">${myQuote.yearly_1x_price.toFixed(2)}/yr</p>
                    )}
                  </div>
                  <div className="text-center p-2.5 rounded-lg border border-[#39FF14]/30 bg-[#39FF14]/5">
                    <p className="font-bold text-white text-sm">2x / month</p>
                    <p className="text-[#A1A1AA] text-xs">Two cuts per month</p>
                    {myQuote?.yearly_2x_price && (
                      <p className="text-[#39FF14] text-sm font-bold mt-1">${myQuote.yearly_2x_price.toFixed(2)}/yr</p>
                    )}
                  </div>
                </div>
                {!(myQuote?.yearly_1x_price || myQuote?.yearly_2x_price) && (
                  <p className="text-[#A1A1AA] text-sm text-center mb-4">Contact us for pricing</p>
                )}
                <ul className="space-y-2 mb-6">
                  {plans.yearly?.features?.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-[#A1A1AA] text-sm">
                      <CheckCircle className="w-4 h-4 text-[#39FF14]" />
                      {feature}
                    </li>
                  ))}
                  <li className="flex items-center gap-2 text-yellow-400 text-sm">
                    <Pause className="w-4 h-4" />
                    Pause anytime (1 month max)
                  </li>
                </ul>
                <Button
                  onClick={() => handleSubscribe("yearly")}
                  disabled={requestingSubscription || processingPayment}
                  className="w-full bg-[#39FF14] text-black hover:bg-[#32D711] font-bold shadow-[0_0_15px_rgba(57,255,20,0.3)] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {requestingSubscription || processingPayment ? (
                    <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2" />
                  ) : (
                    <CreditCard className="w-4 h-4 mr-2" />
                  )}
                  {subscription?.plan === "monthly" ? "Upgrade to Yearly" : "Subscribe Yearly"}
                </Button>
              </div>
            </div>
          </section>
        )}

        {/* Additional Services */}
        {additionalServicesEnabled && (
          <section className="mt-8" data-testid="portal-additional-services">
            <div className="bg-[#121212] border border-[#27272A] rounded-xl p-6">
              <div className="mb-5">
                <h2 className="font-heading text-lg font-bold text-white uppercase flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#39FF14]" />
                  {cms("additional_services", "section_title", "Additional Services")}
                </h2>
                <p className="text-[#A1A1AA] text-sm mt-1">{cms("additional_services", "section_subtitle", "We offer more than just lawn care")}</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* Trash Bin Cleaning */}
                <div className="bg-[#0A0A0A] border border-[#27272A] rounded-lg overflow-hidden hover:border-[#39FF14]/30 transition-all" data-testid="portal-service-trash-bin">
                  <div className="h-32 overflow-hidden relative">
                    <img 
                      src="https://images.unsplash.com/photo-1605602560709-bd03f0366628?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85"
                      alt="Trash Bin Cleaning"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent"></div>
                    <div className="absolute top-2 left-2 w-8 h-8 rounded-md bg-[#39FF14]/20 backdrop-blur-sm flex items-center justify-center">
                      <Trash2 className="w-4 h-4 text-[#39FF14]" />
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="text-white font-bold text-sm mb-1">{cms("additional_services", "trash_bin_title", "Trash Bin Cleaning")}</h3>
                    <p className="text-[#A1A1AA] text-xs mb-3">{cms("additional_services", "trash_bin_desc", "Professional deep cleaning and sanitizing of your trash bins")}</p>
                    <Button
                      size="sm"
                      className="w-full bg-[#39FF14] text-black hover:bg-[#32D711] font-bold text-xs"
                      onClick={() => { setShowCutRequest(true); setCutRequestData(prev => ({...prev, notes: "Service: Trash Bin Cleaning"})); }}
                      data-testid="request-trash-bin"
                    >
                      {cms("additional_services", "request_button", "Request This Service")}
                    </Button>
                  </div>
                </div>
                {/* Gutter Cleaning */}
                <div className="bg-[#0A0A0A] border border-[#27272A] rounded-lg overflow-hidden hover:border-[#39FF14]/30 transition-all" data-testid="portal-service-gutter">
                  <div className="h-32 overflow-hidden relative">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_e3adbda9-f4db-46cf-a8ca-cf2d8ccdb7ef/artifacts/ps0zpixd_800x533_ea1e9d427fb5664c32c517a73e421e58_5303067777d093e6b9c5ec2e6da131d9c0f02fcf.jpg"
                      alt="Gutter Cleaning"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent"></div>
                    <div className="absolute top-2 left-2 w-8 h-8 rounded-md bg-[#39FF14]/20 backdrop-blur-sm flex items-center justify-center">
                      <Droplets className="w-4 h-4 text-[#39FF14]" />
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="text-white font-bold text-sm mb-1">{cms("additional_services", "gutter_title", "Gutter & Drainage Cleaning")}</h3>
                    <p className="text-[#A1A1AA] text-xs mb-3">{cms("additional_services", "gutter_desc", "High-pressure washing of gutters and drainage channels")}</p>
                    <Button
                      size="sm"
                      className="w-full bg-[#39FF14] text-black hover:bg-[#32D711] font-bold text-xs"
                      onClick={() => { setShowCutRequest(true); setCutRequestData(prev => ({...prev, notes: "Service: Gutter & Drainage Cleaning"})); }}
                      data-testid="request-gutter"
                    >
                      {cms("additional_services", "request_button", "Request This Service")}
                    </Button>
                  </div>
                </div>
                {/* Pressure Washing */}
                <div className="bg-[#0A0A0A] border border-[#27272A] rounded-lg overflow-hidden hover:border-[#39FF14]/30 transition-all" data-testid="portal-service-pressure-wash">
                  <div className="h-32 overflow-hidden relative">
                    <img 
                      src="https://images.unsplash.com/photo-1692133188474-8c5591e6a6a8?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85"
                      alt="Pressure Washing"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent"></div>
                    <div className="absolute top-2 left-2 w-8 h-8 rounded-md bg-[#39FF14]/20 backdrop-blur-sm flex items-center justify-center">
                      <Droplets className="w-4 h-4 text-[#39FF14]" />
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="text-white font-bold text-sm mb-1">{cms("additional_services", "pressure_wash_title", "Pressure Washing")}</h3>
                    <p className="text-[#A1A1AA] text-xs mb-3">{cms("additional_services", "pressure_wash_desc", "Professional high-pressure cleaning for driveways, patios, and outdoor surfaces")}</p>
                    <Button
                      size="sm"
                      className="w-full bg-[#39FF14] text-black hover:bg-[#32D711] font-bold text-xs"
                      onClick={() => { setShowCutRequest(true); setCutRequestData(prev => ({...prev, notes: "Service: Pressure Washing"})); }}
                      data-testid="request-pressure-wash"
                    >
                      {cms("additional_services", "request_button", "Request This Service")}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Contact Section with Chat */}
        <section className="mt-8 p-6 bg-[#121212] border border-[#27272A] rounded-xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-[#39FF14]" />
              {cms("contact", "messages_title", "Messages")}
            </h3>
            <Button
              variant="ghost"
              size="sm"
              className="text-[#39FF14] hover:bg-[#39FF14]/10"
              onClick={() => setShowChat(!showChat)}
            >
              {showChat ? cms("contact", "hide_chat", "Hide Chat") : cms("contact", "open_chat", "Open Chat")}
              {messages.filter(m => m.sender_type === 'admin').length > 0 && !showChat && (
                <span className="ml-2 w-2 h-2 bg-[#39FF14] rounded-full animate-pulse"></span>
              )}
            </Button>
          </div>
          
          {showChat ? (
            <div className="space-y-4">
              {/* Messages List */}
              <div className="h-64 overflow-y-auto space-y-3 p-4 bg-[#0A0A0A] rounded-lg">
                {messages.length === 0 ? (
                  <div className="text-center text-[#A1A1AA] py-8">
                    <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No messages yet</p>
                    <p className="text-sm">{cms("contact", "send_message_prompt", "Send a message to Zig Zag Lawncare")}</p>
                  </div>
                ) : (
                  messages.map((msg, idx) => (
                    <div
                      key={msg.message_id || idx}
                      className={`flex ${msg.sender_type === 'customer' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[80%] rounded-lg px-4 py-2 ${
                        msg.sender_type === 'customer'
                          ? 'bg-[#39FF14] text-black'
                          : 'bg-[#27272A] text-white'
                      }`}>
                        <p className="text-sm">{msg.message}</p>
                        <p className={`text-xs mt-1 ${msg.sender_type === 'customer' ? 'text-black/60' : 'text-[#A1A1AA]'}`}>
                          {msg.sender_type === 'admin' ? 'Zig Zag Lawncare' : 'You'} • {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  ))
                )}
                <div ref={chatEndRef} />
              </div>
              
              {/* Message Input */}
              {myCustomer?.customer_id ? (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type your message..."
                    className="flex-1 bg-[#0A0A0A] border border-[#27272A] rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#39FF14]"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                  />
                  <Button
                    onClick={handleSendMessage}
                    disabled={sendingMessage || !newMessage.trim()}
                    className="bg-[#39FF14] text-black hover:bg-[#32D711] px-4"
                  >
                    {sendingMessage ? (
                      <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              ) : (
                <p className="text-[#A1A1AA] text-sm text-center">{cms("contact", "profile_prompt", "Please complete your profile to send messages")}</p>
              )}
            </div>
          ) : (
            <div className="flex items-start gap-4">
              <img 
                src={LOGO_URL} 
                alt="Zig Zag Lawncare" 
                className="h-16 w-auto object-contain rounded-lg shrink-0"
              />
              <div>
                <p className="text-[#A1A1AA] text-sm mb-4">
                  {cms("contact", "contact_prompt", "Questions? Send us a message or reach out on Facebook.")}
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="bg-[#39FF14] text-black hover:bg-[#32D711]"
                    onClick={() => setShowChat(true)}
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Chat with Us
                  </Button>
                  <a 
                    href="https://www.facebook.com/zigzagokinawa" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-[#1877F2] text-[#1877F2] hover:bg-[#1877F2] hover:text-white"
                    >
                      <Facebook className="w-4 h-4 mr-2" />
                      Facebook
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          )}
        </section>
      </main>

      {/* Calendar Modal */}
      <AddToCalendarModal 
        isOpen={calendarModalOpen}
        onClose={closeCalendarModal}
        job={selectedJob}
      />

      {/* Booking Wizard Modal */}
      <BookingWizard 
        isOpen={showBookingWizard} 
        onClose={() => setShowBookingWizard(false)} 
        defaultAddress={myCustomer?.address || ""}
      />

      <AddressGate
        open={needsAddress}
        onSaved={(address) => setMyCustomer((c) => ({ ...(c || {}), address }))}
      />
      <QuotePromptDialog
        open={showQuotePrompt && !needsAddress}
        onClose={closeQuotePrompt}
        onStart={() => { closeQuotePrompt(); setShowBookingWizard(true); }}
      />

      {/* Reschedule Dialog */}
      <Dialog open={!!rescheduleJob} onOpenChange={(open) => { if (!open) setRescheduleJob(null); }}>
        <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase">Reschedule Appointment</DialogTitle>
          </DialogHeader>
          {rescheduleJob && (
            <div className="space-y-4">
              <div className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-3">
                <p className="text-[#A1A1AA] text-xs">Current Date</p>
                <p className="text-white font-medium">
                  {rescheduleJob.scheduled_date ? format(parseISO(rescheduleJob.scheduled_date), "EEEE, MMMM d, yyyy") : "Unknown"}
                </p>
                <p className="text-[#A1A1AA] text-sm capitalize">{rescheduleJob.scheduled_time}</p>
              </div>
              <div>
                <Label className="text-[#A1A1AA]">New Date</Label>
                <Input
                  data-testid="reschedule-date-input"
                  type="date"
                  value={rescheduleDate}
                  onChange={e => setRescheduleDate(e.target.value)}
                  className="bg-[#0A0A0A] border-[#27272A] text-white"
                  min={format(new Date(), "yyyy-MM-dd")}
                />
              </div>
              <div>
                <Label className="text-[#A1A1AA]">Preferred Time</Label>
                <Select value={rescheduleTime} onValueChange={setRescheduleTime}>
                  <SelectTrigger className="bg-[#0A0A0A] border-[#27272A] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-[#27272A]">
                    <SelectItem value="morning" className="text-white hover:bg-[#27272A]">Morning (8am-12pm)</SelectItem>
                    <SelectItem value="afternoon" className="text-white hover:bg-[#27272A]">Afternoon (12pm-4pm)</SelectItem>
                    <SelectItem value="evening" className="text-white hover:bg-[#27272A]">Evening (4pm-7pm)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <p className="text-yellow-400 text-xs">Your rescheduled appointment will need admin approval before it's confirmed.</p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1 border-[#27272A] text-[#A1A1AA]"
                  onClick={() => setRescheduleJob(null)}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-yellow-400 text-black hover:bg-yellow-500 font-bold"
                  onClick={handleReschedule}
                  disabled={!rescheduleDate || submittingReschedule}
                  data-testid="confirm-reschedule-btn"
                >
                  {submittingReschedule ? "Submitting..." : "Request Reschedule"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerPortal;
